/**
 * Group code validation pattern and utilities
 * Group codes are URL-friendly identifiers for groups (e.g., "pack-400", "troop-400g")
 */

/**
 * Pattern for valid group codes:
 * - Must start with a letter
 * - Can contain lowercase letters, numbers, and hyphens
 * - No leading or trailing hyphens
 * - No consecutive hyphens
 */
export const GROUP_CODE_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/**
 * Error message for invalid group codes
 */
export const GROUP_CODE_ERROR_MESSAGE =
  "Group code must start with a letter, contain only lowercase letters, numbers, and hyphens, with no leading/trailing hyphens (e.g., 'pack-400')";

/**
 * Validate a group code format
 * @param code - The group code to validate
 * @returns true if valid, false otherwise
 */
export function isValidGroupCode(code: string): boolean {
  return GROUP_CODE_PATTERN.test(code);
}

/**
 * Validate a group code and throw if invalid
 * @param code - The group code to validate
 * @throws Error if code is invalid
 */
export function validateGroupCode(code: string): void {
  if (!isValidGroupCode(code)) {
    throw new Error(GROUP_CODE_ERROR_MESSAGE);
  }
}

/**
 * Generate a suggested group code from a group name
 * @param name - The group name
 * @returns A suggested group code
 */
export function generateCodeFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 50);
}

